import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Bot, Power, Activity, Zap, CheckCircle2, Clock, AlertCircle,
  Mail, FileText, Gavel, Search, Send, RefreshCw, Rocket, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_META = {
  auto_match:    { icon: Search, label: 'Match',         color: 'text-blue-400'   },
  auto_dispatch: { icon: Mail,   label: 'Dispatch',      color: 'text-purple-400' },
  auto_bid:      { icon: Gavel,  label: 'Auto-Bid',      color: 'text-amber-400'  },
  auto_accept:   { icon: CheckCircle2, label: 'Auto-Confirm', color: 'text-green-400'  },
  auto_rate_conf:{ icon: FileText, label: 'Rate Conf.',  color: 'text-cyan-400'   },
  auto_backhaul: { icon: Send,   label: 'Backhaul',      color: 'text-pink-400'   },
  auto_invoice:  { icon: DollarSign, label: 'Invoice',   color: 'text-emerald-400'},
};

const StatusDot = ({ status }) => {
  const color = status === 'success' ? 'bg-green-500'
    : status === 'skipped' ? 'bg-amber-500'
    : 'bg-red-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
};

const AutopilotPage = () => {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, st, a] = await Promise.all([
        axios.get(`${API}/autopilot/settings`),
        axios.get(`${API}/autopilot/stats`),
        axios.get(`${API}/autopilot/activity?limit=50`),
      ]);
      setSettings(s.data);
      setStats(st.data);
      setActivity(a.data.activity || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 5000); // live refresh
    return () => clearInterval(id);
  }, [fetchAll]);

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/autopilot/settings`, { [key]: value });
      setSettings(res.data);
      if (key === 'master_enabled') {
        toast.success(value ? 'Autopilot ENGAGED — running autonomously' : 'Autopilot disengaged');
      } else {
        toast.success('Setting saved');
      }
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const triggerScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/autopilot/scan`);
      toast.success(`Scanning ${r.data.scanned} live loads`);
      setTimeout(fetchAll, 1500);
    } catch (e) {
      toast.error('Scan failed (autopilot disabled?)');
    } finally {
      setScanning(false);
    }
  };

  if (loading || !settings || !stats) {
    return (
      <Layout>
        <div className="p-8 text-center text-muted-foreground">Loading Autopilot...</div>
      </Layout>
    );
  }

  const master = settings.master_enabled;
  const agencyAcked = typeof window !== 'undefined' && window.localStorage.getItem('autopilot-agency-acked') === '1';
  const ackAgency = () => {
    window.localStorage.setItem('autopilot-agency-acked', '1');
    window.location.reload();
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="autopilot-page">
        {!agencyAcked && (
          <Card className="border-amber-500/40 bg-amber-500/10" data-testid="autopilot-agency-consent">
            <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <AlertCircle className="w-6 h-6 text-amber-400 shrink-0" />
              <div className="text-sm text-amber-100 flex-1">
                <strong className="text-amber-200">You are the principal — Tempus is your software agent.</strong>{' '}
                When Autopilot "auto-confirms" a bid, it acts on your pre-configured rules and on your behalf. You retain
                all commercial responsibility for the freight contract. Tempus Dispatch is a SaaS platform, not a broker,
                and does not hold FMCSA Broker Authority.
              </div>
              <Button
                size="sm"
                onClick={ackAgency}
                data-testid="autopilot-ack-agency-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                I understand
              </Button>
            </CardContent>
          </Card>
        )}
        {/* HERO TOGGLE */}
        <Card className={`overflow-hidden border-2 ${master ? 'border-green-500/60 shadow-[0_0_60px_rgba(34,197,94,0.25)]' : 'border-border'}`}>
          <CardContent className="p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${master ? 'bg-green-500/20' : 'bg-muted'}`}>
                  <Rocket className={`w-8 h-8 ${master ? 'text-green-400' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-3xl lg:text-4xl font-bold">Autopilot</h1>
                    <Badge variant={master ? 'default' : 'secondary'} className={master ? 'bg-green-500/20 text-green-300 border-green-500/30' : ''}>
                      {master ? 'ENGAGED' : 'STANDBY'}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm max-w-md">
                    Your software agent. Runs Match → Dispatch → Bid → <em>Confirm on your behalf</em> → Rate Conf → Invoice using only the rules YOU set. Tempus is not a broker — you remain the principal.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={triggerScan}
                  disabled={!master || scanning}
                  data-testid="autopilot-scan-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
                  Scan now
                </Button>
                <button
                  type="button"
                  onClick={() => updateSetting('master_enabled', !master)}
                  disabled={saving}
                  data-testid="autopilot-master-toggle"
                  className={`relative inline-flex items-center h-14 w-28 rounded-full transition-colors ${master ? 'bg-green-500' : 'bg-muted'}`}
                >
                  <Power className="absolute left-3 w-5 h-5 text-white/70" />
                  <span
                    className={`inline-block w-12 h-12 rounded-full bg-white shadow-lg transform transition-transform ${master ? 'translate-x-14' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-actions">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <Activity className="w-3.5 h-3.5" /> Actions (24h)
              </div>
              <div className="text-3xl font-bold">{stats.actions_last_24h}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-pipelines">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <Zap className="w-3.5 h-3.5" /> Pipelines
              </div>
              <div className="text-3xl font-bold">{stats.pipelines_started}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.pipelines_completed} completed · {stats.pipelines_in_progress} live
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-accepted">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Auto-Confirmed
              </div>
              <div className="text-3xl font-bold text-green-400">{stats.auto_accepted_loads}</div>
              <div className="text-xs text-muted-foreground mt-1">loads (on your behalf)</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-gmv">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <DollarSign className="w-3.5 h-3.5" /> Freight Routed (GMV)
              </div>
              <div className="text-3xl font-bold">${Math.round(stats.auto_gmv || 0).toLocaleString()}</div>
              <div className="text-xs text-zinc-500 mt-1">
                Paid directly broker → carrier (Tempus does not hold funds)
              </div>
            </CardContent>
          </Card>
        </div>

        {/* PIPELINE STEPS & SETTINGS */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5" /> Pipeline Steps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                ['auto_match_enabled',           Search,        'Auto-Match Drivers',     'Score & rank top drivers per equipment + lane'],
                ['auto_dispatch_email_enabled',  Mail,          'Auto-Dispatch Emails',   'Email matched drivers with load offer'],
                ['auto_process_autobids_enabled',Gavel,         'Trigger Auto-Bid Rules', 'Fire standing-order bids from drivers'],
                ['auto_accept_enabled',          CheckCircle2,  'Auto-Confirm on Your Behalf',   'Acts as YOUR software agent — executes your pre-set rules to confirm the winning bid. Tempus is not a broker; you remain the principal in the freight contract.'],
                ['auto_generate_rate_conf_enabled', FileText,   'Generate Rate Confirmation', 'Auto-create + email RC to winning driver'],
                ['auto_backhaul_enabled',        Send,          'Backhaul Auto-Match',    'Email driver return-trip loads to kill empty miles'],
                ['auto_generate_invoice_enabled',DollarSign,    'Generate Invoice',       'On delivery → invoice the broker (broker pays carrier directly)'],
              ].map(([key, Icon, label, desc]) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-primary" />
                    <div>
                      <div className="font-medium text-sm">{label}</div>
                      <div className="text-xs text-muted-foreground">{desc}</div>
                    </div>
                  </div>
                  <Switch
                    checked={settings[key]}
                    onCheckedChange={(v) => updateSetting(key, v)}
                    disabled={saving}
                    data-testid={`toggle-${key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" /> Behavior
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Bid window (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  value={settings.bid_window_minutes}
                  onChange={(e) => setSettings({ ...settings, bid_window_minutes: parseInt(e.target.value || 0, 10) })}
                  onBlur={(e) => updateSetting('bid_window_minutes', parseInt(e.target.value || 0, 10))}
                  data-testid="input-bid-window"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Time to wait before picking the best bid.</p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
                <div>
                  <div className="text-sm font-medium">Instant-book</div>
                  <div className="text-xs text-muted-foreground">Accept bids at/below posted rate immediately</div>
                </div>
                <Switch
                  checked={settings.instant_book_at_or_below_rate}
                  onCheckedChange={(v) => updateSetting('instant_book_at_or_below_rate', v)}
                />
              </div>

              <div className="p-3 rounded-lg bg-muted/40 space-y-1.5" data-testid="min-tier-row">
                <div className="text-sm font-medium">Minimum Trust tier for Auto-Confirm</div>
                <div className="text-xs text-muted-foreground">
                  Carriers below this tier require manual confirmation before a rate confirmation is sent.
                </div>
                <select
                  value={settings.min_trust_tier_for_auto_confirm || 'verified'}
                  onChange={(e) => updateSetting('min_trust_tier_for_auto_confirm', e.target.value)}
                  className="mt-2 w-full bg-background border border-border rounded-md px-2 py-1.5 text-sm"
                  data-testid="select-min-tier"
                >
                  <option value="verified">Verified (default)</option>
                  <option value="top10">Top 10%</option>
                  <option value="elite">Elite</option>
                </select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Min driver match score</Label>
                <Input
                  type="number"
                  min={0} max={100}
                  value={settings.min_driver_match_score}
                  onChange={(e) => setSettings({ ...settings, min_driver_match_score: parseInt(e.target.value || 0, 10) })}
                  onBlur={(e) => updateSetting('min_driver_match_score', parseInt(e.target.value || 0, 10))}
                />
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Max drivers to email</Label>
                <Input
                  type="number"
                  min={1} max={100}
                  value={settings.max_drivers_to_email}
                  onChange={(e) => setSettings({ ...settings, max_drivers_to_email: parseInt(e.target.value || 1, 10) })}
                  onBlur={(e) => updateSetting('max_drivers_to_email', parseInt(e.target.value || 1, 10))}
                />
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Scan interval (sec)</Label>
                <Input
                  type="number"
                  min={15} max={3600}
                  value={settings.scan_interval_seconds}
                  onChange={(e) => setSettings({ ...settings, scan_interval_seconds: parseInt(e.target.value || 60, 10) })}
                  onBlur={(e) => updateSetting('scan_interval_seconds', parseInt(e.target.value || 60, 10))}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* LIVE ACTIVITY FEED */}
        <Card data-testid="autopilot-activity-feed">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Activity className="w-5 h-5" /> Live Activity</span>
              <span className="text-xs text-muted-foreground font-normal">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                Auto-refreshing every 5s
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bot className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No autopilot activity yet.</p>
                <p className="text-xs mt-1">Engage the autopilot, post a load — the AI will do the rest.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {activity.map((row) => {
                  const meta = STEP_META[row.step] || { icon: Activity, label: row.step, color: 'text-muted-foreground' };
                  const Icon = meta.icon;
                  return (
                    <div key={row.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-sm">
                      <Icon className={`w-4 h-4 mt-0.5 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusDot status={row.status} />
                          <span className="font-medium">{meta.label}</span>
                          <code className="text-[10px] text-muted-foreground">{row.load_id?.slice(0, 8)}</code>
                        </div>
                        <p className="text-muted-foreground mt-0.5">{row.message}</p>
                      </div>
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {new Date(row.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AutopilotPage;
